import apiClient from "./apiClient";

const browserOrigin = () => window.location.origin;

// Parse before deciding trust. Ports, schemes, userinfo and normalized hostnames
// must not inherit trust from a textual prefix.
export const documentRequest = (value, base = apiClient.defaults.baseURL || "") => {
  if (typeof value !== "string" || !value.trim() ||
      Array.from(value).some((char) => char === "\\" || char.charCodeAt(0) < 32 || char.charCodeAt(0) === 127)) {
    throw new Error("Invalid document URL.");
  }
  const apiBase = new URL(base || "/", browserOrigin());
  const url = new URL(value, `${apiBase.href.replace(/\/+$/, "")}/`);
  if (!["http:", "https:"].includes(url.protocol) || url.username || url.password) {
    throw new Error("Unsupported document URL.");
  }
  const trusted = url.origin === apiBase.origin || url.origin === browserOrigin();
  if (!trusted && url.protocol !== "https:") {
    throw new Error("External document URLs must use HTTPS.");
  }
  return { url: url.href, trusted };
};

export const isTrustedDocumentUrl = (value, base) => {
  try {
    return documentRequest(value, base).trusted;
  } catch (_error) {
    return false;
  }
};

export const resolveSafeDocumentUrl = (value) => {
  try {
    return documentRequest(value).url;
  } catch (_error) {
    return null;
  }
};

// Existing fetch-based previews use cookie auth only. Do not introduce a bearer
// header, inherit shared-client defaults, or follow opaque HTTP redirects.
export const fetchDocument = async (value, { allowLocalPreview = false } = {}) => {
  // Preserve policy previews of browser-local data without granting network auth.
  if (allowLocalPreview && typeof value === "string") {
    const local = new URL(value, browserOrigin());
    if (local.protocol === "data:" || (local.protocol === "blob:" && local.origin === browserOrigin())) {
      return fetch(local.href, { credentials: "omit", redirect: "error", referrerPolicy: "no-referrer" });
    }
  }
  const { url, trusted } = documentRequest(value);
  return fetch(url, {
    credentials: trusted ? "include" : "omit",
    redirect: "error",
    referrerPolicy: "no-referrer",
  });
};

export const getDocumentBlobResponse = async (value, base) => {
  const { url, trusted } = documentRequest(value, base);
  if (trusted) {
    // XHR follows redirects without an opt-out. Pin fetch (no XHR fallback) so
    // legacy bearer headers and cookies cannot follow an HTTP Location header.
    return apiClient.get(url, {
      adapter: "fetch",
      fetchOptions: { redirect: "error", referrerPolicy: "no-referrer" },
      responseType: "blob",
      withCredentials: true,
      skipAuthRedirect: true,
      validateStatus: () => true,
    });
  }
  // S3/custom storage uses only its signed URL, never the authenticated client.
  const response = await fetchDocument(url);
  return {
    status: response.status,
    headers: { "content-type": response.headers.get("content-type") || "" },
    data: await response.blob(),
  };
};
